import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../app/store';
import { Skill } from '../../models/skill';
import { skillTrainerData } from './skillTrainerData';
import { skillTrainerCalc, SkillTrainerResult } from './skillTrainerCalc';
import { skillTrainerCalc2 } from './skillTrainerCalc2';
import { Profession } from '../../models/profession';
import { Race } from '../../models/race';
import { loadBuildFulfilled } from './skillLoadSlice';
import { decodeBuildFulfilled } from './skillShareSlice';

const data = {
  races: skillTrainerData.getRaces(),
  professions: skillTrainerData.getProfessions(),
  skills: skillTrainerData.getSkills()
};

let getTrainerResultsTimeoutId: NodeJS.Timeout | undefined;

const calcState = (state: SkillTrainerState) => {
  calcProfessions(state);
  calcStats(state);
  calcBonded(state);
  return state;
}

const calcProfessions = (state: SkillTrainerState) => {
  const filtered = state.professions.filter(x => !state.raceId || x.raceIds.includes(state.raceId));
  let valid = filtered.some(x => x.id === state.professionId);  
  if (!valid) {
    state.professionId = filtered[0].id ?? '';
  }
  state.professionOptions = filtered;
}

const calcStats = (state: SkillTrainerState) => {
  if (!state.statsDirty) {
    switch (state.professionId) {
      case 'warrior':
        if (state.raceId !== 'trolloc') {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 12, 12, 18, 19];
        } else {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 7, 7, 17, 19];
        }
        break;
      case 'rogue':
        if (state.raceId !== 'trolloc') {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [18, 12, 12, 19, 19];
        } else {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [17, 7, 7, 19, 19];
        }
        break;
      case 'hunter':
        if (state.raceId !== 'trolloc') {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [18, 12, 12, 19, 19];
        } else {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 7, 7, 17, 19];
        }
        break;
      case 'fchanneler':
        [state.strn, state.intl, state.wilp, state.dext, state.cont] = [14, 18, 18, 18, 14];
        break;
      case 'mchanneler':
        [state.strn, state.intl, state.wilp, state.dext, state.cont] = [16, 18, 18, 18, 14];
        break;
      case 'fade':
        [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 12, 12, 18, 19];
        break;
    }
  }
}

const calcBonded = (state: SkillTrainerState) => {  
  let valid = state.raceId === 'human';
  valid &&= ['warrior', 'rogue', 'hunter'].includes(state.professionId);
  if (!valid) {
    state.bonded = false;
  }
  state.bondedEnabled = valid;
}

export const getTrainerResults = (): AppThunk => (dispatch, getState) => {  
  cancelPendingGetTrainerResults();
  dispatch(slice.actions.getTrainerResultsPending());
  getTrainerResultsTimeoutId = setTimeout(async () => {
    try {

      let results: SkillTrainerResult;
      if (getState().skillTrainer.optimized) {
        results = skillTrainerCalc2.getTrainerResults(getState().skillTrainer) as SkillTrainerResult;
      } else {
        results = skillTrainerCalc.getTrainerResults(getState().skillTrainer);
      }
      
      dispatch(slice.actions.getTrainerResultsFulfilled(results));
    } catch (ex) {
      dispatch(slice.actions.getTrainerResultsRejected());
    }
  }, 300);
};

const cancelPendingGetTrainerResults = () => {
  clearTimeout(getTrainerResultsTimeoutId);
}

const initialState: SkillTrainerState = calcState({
  strn: 19,
  intl: 12,
  wilp: 12,
  dext: 18,
  cont: 19,
  statsDirty: false,
  races: data.races,
  raceOptions: data.races,
  raceId: 'human',
  professions: data.professions,
  professionOptions: data.professions,
  professionId: 'warrior',
  bonded: false,
  bondedEnabled: true,
  skills: data.skills,
  getTrainerResultsTrigger: 0,
  commands: { warrior: '', rogue: '', hunter: '', fade: '' },
  pracs: 0,
  remainderPracs: 0,
  level: 1,
  residualOrderIds: [],
  residualOrderType: '',
  optimized: false
});

export interface SkillTrainerState {
  strn: number;
  intl: number;
  wilp: number;
  dext: number;
  cont: number;
  statsDirty: boolean;
  races: Race[];
  raceOptions: Race[];
  raceId: string;
  professions: Profession[];
  professionOptions: Profession[];
  professionId: string;
  bonded: boolean;
  bondedEnabled: boolean;
  skills: Skill[];
  getTrainerResultsTrigger: number;
  pracs: number;
  remainderPracs: number;
  level: number;
  residualOrderIds: string[];
  residualOrderType: string;
  commands: { [key: string]: string };
  optimized: boolean;
}

const slice = createSlice({
  name: 'skillTrainer',
  initialState,
  reducers: {
    setOptimized: (state, action: PayloadAction<boolean>) => {
      state.optimized = action.payload;
    },

    setStrn: (state, action: PayloadAction<number>) => {
      state.strn = action.payload;
      state.statsDirty = true;
    },

    setIntl: (state, action: PayloadAction<number>) => {
      state.intl = action.payload;
      state.statsDirty = true;
    },

    setWilp: (state, action: PayloadAction<number>) => {
      state.wilp = action.payload;
      state.statsDirty = true;
    },

    setDext: (state, action: PayloadAction<number>) => {
      state.dext = action.payload;
      state.statsDirty = true;
    },

    setCont: (state, action: PayloadAction<number>) => {
      state.cont = action.payload;
      state.statsDirty = true;
    },
    
    setRaceId: (state, action: PayloadAction<string>) => {
      state.raceId = action.payload;
      calcProfessions(state);
      calcStats(state);
      calcBonded(state);      
    },

    setProfessionId: (state, action: PayloadAction<string>) => {
      state.professionId = action.payload;      
      calcProfessions(state);
      calcStats(state);
      calcBonded(state);
    },

    setBonded: (state, action: PayloadAction<boolean>) => {
      state.bonded = action.payload;
      calcBonded(state);
    },

    copySkill: (state, action: PayloadAction<string>) => {
      //
    },

    setSkillTargetPercent: (state, action: PayloadAction<{skillId: string, percent: number}>) => {
      const { skillId, percent } = action.payload;
      const skill = state.skills.find(x => x.id === skillId);
      if (skill) {
        skill.percentTarget = percent;
        skill.percentActual = 0;
        skill.pracsActual = 0;
        skill.percentHistory = [];
        skill.busy = true;
      }
    },

    setSkillStartPercent: (state, action: PayloadAction<{skillId: string, percent: number}>) => {
      const { skillId, percent } = action.payload;
      const skill = state.skills.find(x => x.id === skillId);
      if (skill) {
        skill.percentStart = percent;
        skill.percentActual = 0;
        skill.pracsActual = 0;
        skill.percentHistory = [];
        skill.busy = true;
      }
    },

    reset: (state) => {
      cancelPendingGetTrainerResults();
      state.level = 1;
      state.pracs = 0;
      state.remainderPracs = 0;
      state.commands = { warrior: '', rogue: '', hunter: '', fade: '' };
      
      for (let skill of state.skills) {
        skill.percentTarget = 0;
        skill.percentStart = 0;
        skill.percentActual = 0;
        skill.pracsActual = 0;
        skill.percentHistory = [];
        skill.busy = false;
      }
    },

    getTrainerResultsPending: (state) => {
      if (state.skills.some(x => x.percentTarget)) {
        state.statsDirty = true;
      }
    },
    
    getTrainerResultsFulfilled: (state, action: PayloadAction<SkillTrainerResult>) => {
      const result = action.payload;
      state.commands = result.commands;
      state.pracs = result.pracs;
      state.level = result.level;
      state.residualOrderIds = result.residualOrderIds;
      state.residualOrderType = result.residualOrderType;
      state.remainderPracs = result.remainderPracs;

      for (let skill of state.skills) {
        skill.busy = false;
      }
      
      for (let skillResult of result.skills) {
        const skill = state.skills.find(x => x.id === skillResult.id);
        if (skill) {
          skill.percentActual = skillResult.percentActual;
          skill.pracsActual = skillResult.pracsActual;
          skill.percentHistory = skillResult.percentHistory;
          skill.pracsCount = skillResult.pracsCount;
        }      
      }
    },

    getTrainerResultsRejected: (state) => {
      for (let skill of state.skills) {
        skill.busy = false;
      }
    }  
  },
  
  extraReducers: builder => {
    builder.addCase(loadBuildFulfilled, (state, action) => {
      slice.caseReducers.reset(state);

      state.strn = action.payload.strn;
      state.intl = action.payload.intl;
      state.wilp = action.payload.wilp;
      state.dext = action.payload.dext;
      state.cont = action.payload.cont;
      state.raceId = action.payload.raceId;
      state.professionId = action.payload.professionId;
      
      for (let savedSkill of action.payload.skills) {
        const skill = state.skills.find(x => x.id === savedSkill.id);
        if (skill) {
          skill.percentStart = 0;
          skill.percentTarget = savedSkill.percentTarget;
        }
      }
    });

    builder.addCase(decodeBuildFulfilled, (state, action) => {
      slice.caseReducers.reset(state);

      const race = state.races.find(x => x.idNum.toString() === action.payload.raceId);
      const profession = state.professions.find(x => x.idNum.toString() === action.payload.professionId);      
      state.strn = action.payload.strn ?? 0;
      state.intl = action.payload.intl ?? 0;
      state.wilp = action.payload.wilp ?? 0;
      state.dext = action.payload.dext ?? 0;
      state.cont = action.payload.cont ?? 0;
      state.raceId = race?.id ?? '';
      state.professionId = profession?.id ?? '';

      for (let savedSkill of action.payload.skills) {
        const skill = state.skills.find(x => x.idNum === savedSkill.idNum);
        if (skill) {
          skill.percentStart = 0;
          skill.percentTarget = savedSkill.percentTarget ?? 0;
        }
      }
    });
  }
});

export default slice.reducer;
export const { reset, setStrn, setIntl, setWilp, setDext, setCont, setRaceId, setProfessionId, setBonded, setSkillTargetPercent, setSkillStartPercent, setOptimized } = slice.actions;

//TO DO: add getTrainerResultsPending, getTrainerResultsRejected